import { useState, useRef, useEffect } from "react";
import { Star } from "lucide-react";

/**
 * A single Google review.
 *
 * Review length is entirely outside our control — Google returns whatever the
 * customer wrote, and one 250-word review among four short ones stretches its
 * grid row and leaves the neighbouring cards as tall columns of empty space.
 * The text is therefore clamped to a readable length, with an expander for
 * anyone who wants the rest.
 *
 * The expander only appears when the text is genuinely truncated, measured
 * after render rather than guessed from character count — the same string
 * wraps to a different number of lines at different breakpoints.
 */
export default function ReviewCard({ review }) {
  const [expanded, setExpanded] = useState(false);
  const [isTruncated, setIsTruncated] = useState(false);
  const textRef = useRef(null);

  useEffect(() => {
    const element = textRef.current;
    if (!element) return;

    const measure = () => {
      setIsTruncated(element.scrollHeight > element.clientHeight + 1);
    };

    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [review.text]);

  return (
    <div className="bg-charcoal/40 border border-charcoal p-6 md:p-7 flex flex-col h-full">
      <div className="flex items-center gap-1 text-emerald">
        {Array.from({ length: review.rating }).map((_, index) => (
          <Star key={index} className="w-4 h-4 fill-current" />
        ))}
      </div>

      <p
        ref={textRef}
        className={`mt-4 text-white/90 text-sm md:text-base leading-relaxed flex-1 ${
          expanded ? "" : "line-clamp-[8]"
        }`}
      >
        &ldquo;{review.text}&rdquo;
      </p>

      {isTruncated && (
        <button
          type="button"
          onClick={() => setExpanded((value) => !value)}
          className="mt-3 self-start text-xs uppercase tracking-[0.16em] text-emerald hover:text-white transition-colors"
          data-testid="review-toggle"
        >
          {expanded ? "Show less" : "Read full review"}
        </button>
      )}

      <div className="mt-6 flex items-center justify-between gap-4">
        <div className="text-white font-display font-semibold text-sm">
          {review.reviewerName}
        </div>
        <div className="text-mutedtext text-xs whitespace-nowrap">
          {new Date(review.publishedAt).toLocaleDateString("en-SG", {
            year: "numeric",
            month: "short",
          })}
        </div>
      </div>
    </div>
  );
}
